/** Card + table types shared by server, TV host and phone clients. */
import type { BaccaratOutcome, BaccaratSideBetId, BaccaratSpot } from './baccarat.js';
import type { BaccaratRules } from './baccaratRules.js';
import type { CrapsBetId, CrapsPressMode, CrapsResolution, CrapsRoll, CrapsRollEvent } from './craps.js';
import type { CrapsRules } from './crapsRules.js';
import type { PublicGamePoll } from './gamePoll.js';
import type { PublicParty } from './party.js';
import type { PendingRules } from './pendingRules.js';
import type { HandCategory } from './poker.js';
import type { HoldemOptions, HoldemStreet, PotShare } from './holdem.js';
import type { HoldemRules } from './holdemRules.js';
import type { KenoRules } from './kenoRules.js';
import type { ProfileColorId } from './profile.js';
import type {
  RouletteBetId,
  RouletteBetNum,
  RouletteBetSpec,
  RoulettePocket,
} from './roulette.js';
import type { RouletteRules } from './rouletteRules.js';
import type { TableRules } from './rules.js';
import type { ThreecardCategory, ThreecardOutcome } from './threecard.js';
import type { ThreecardRules } from './threecardRules.js';

export type Suit = 'S' | 'H' | 'D' | 'C';
export type Rank =
  | 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';

export interface Card {
  rank: Rank;
  suit: Suit;
}

/**
 * A card slot in public state. Face-down cards carry no rank/suit at all, so a
 * hole card can never leak through a public broadcast.
 */
export type PublicCard = { faceUp: true; card: Card } | { faceUp: false };

/** Which game the table is dealing. Chosen by the host, in the lobby only. */
export type GameType = 'blackjack' | 'holdem' | 'baccarat' | 'roulette' | 'craps' | 'threecard' | 'keno';

/** Every felt, in the order pickers and the console offer them. */
export const GAME_TYPES: readonly GameType[] = [
  'blackjack',
  'holdem',
  'baccarat',
  'roulette',
  'craps',
  'threecard',
  'keno',
];

/** How each game reads on a button, a row, a badge. */
export const GAME_LABELS: Record<GameType, string> = {
  blackjack: 'Blackjack',
  holdem: "Hold'em",
  baccarat: 'Baccarat',
  roulette: 'Roulette',
  craps: 'Craps',
  threecard: '3-Card Poker',
  keno: 'Keno',
};

/** The catalog as `{ id, label }` rows, for a picker that just maps. */
export const GAME_OPTIONS: readonly { id: GameType; label: string }[] = GAME_TYPES.map(
  (id) => ({ id, label: GAME_LABELS[id] }),
);

export const isGameType = (value: unknown): value is GameType =>
  typeof value === 'string' && (GAME_TYPES as readonly string[]).includes(value);

export const gameLabel = (game: GameType): string => GAME_LABELS[game];

/**
 * Every game shares `lobby`, `betting` and `payout`; the rest belong to one of
 * them. Blackjack runs betting → [insurance] → playing → dealer; Hold'em walks
 * the four streets; baccarat, roulette and keno run betting → dealer, since
 * none has a decision left once the window closes; craps runs betting (the
 * come-out window) → rolling, and stays there for as many rolls as the hand
 * lasts; three-card poker runs betting → playing (each ante decides play or
 * fold) → dealer. `insurance` only ever appears when the dealer shows an ace
 * and the host has insurance switched on.
 */
export type Phase =
  | 'lobby'
  | 'betting'
  | 'insurance'
  | 'playing'
  | 'dealer'
  | 'payout'
  | 'preflop'
  | 'flop'
  | 'turn'
  | 'river'
  | 'rolling';

/** True for the four Hold'em betting streets. */
export const HOLDEM_PHASES: readonly Phase[] = ['preflop', 'flop', 'turn', 'river'];

/** How a settled hand fared against the dealer. */
export type RoundOutcome = 'blackjack' | 'win' | 'push' | 'loss' | 'surrender';

/** Identifies a side bet; the wager rules live in `SIDE_BETS`. */
export type SideBetId =
  | 'perfect_pairs'
  | 'twenty_one_plus_three'
  | 'lucky_lucky'
  | 'suit_em_up'
  | 'royal_match'
  | 'over_13'
  | 'under_13';

/**
 * Seat-level state. Card outcomes live on the hand, not here, because one seat
 * can hold several hands after a split.
 */
export type SeatStatus =
  | 'empty'
  | 'waiting'
  | 'sitting_out'
  | 'betting'
  | 'ready'
  | 'acting'
  | 'done';

/** Per-hand state. A seat starts with one and gains more on every split. */
export type HandStatus = 'live' | 'stood' | 'busted' | 'blackjack' | 'surrendered';

/** What the acting player may legally do with the hand in front of them. */
export interface HandOptions {
  hit: boolean;
  stand: boolean;
  double: boolean;
  split: boolean;
  surrender: boolean;
}

export interface PublicHand {
  /** Visible cards only. Face-down entries are opaque. */
  cards: PublicCard[];
  /** Value of the face-up cards; null when nothing is visible. */
  visibleValue: number | null;
  /** True when the visible total still counts an ace as 11. */
  visibleSoft: boolean;
  /** Chips staked on this hand, doubling included. */
  bet: number;
  status: HandStatus;
  /** Set once the round resolves; null before that. */
  result: RoundOutcome | null;
  /** Chips returned on settle, stake included; null before the round resolves. */
  payout: number | null;
  /** True for every hand produced by a split, including the original's remainder. */
  fromSplit: boolean;
  doubled: boolean;
}

export interface PublicSideBet {
  id: SideBetId;
  stake: number;
  /** Set on settle: the winning combination's name, or null on a loss. */
  label: string | null;
  payout: number | null;
}

export interface PublicSeat {
  index: number;
  playerId: string | null;
  name: string | null;
  connected: boolean;
  /** The owning player's bankroll, mirrored here for the seat display. */
  chips: number;
  /** This round's base stake, before any double or split. */
  bet: number;
  status: SeatStatus;
  hands: PublicHand[];
  /** Which of `hands` holds the action, or null when this seat is not acting. */
  activeHandIndex: number | null;
  /** Legal moves for the active hand; null unless this seat is acting. */
  options: HandOptions | null;
  sideBets: PublicSideBet[];
  /** Chips staked on insurance this round; 0 when none was taken. */
  insuranceBet: number;
  /** Set on settle: chips returned by the insurance wager. */
  insurancePayout: number | null;
  /**
   * The player's standing reservation at the table: true from the moment they
   * say "I'm ready" until they step away. Survives round transitions, unlike
   * `status`, which the round machine rewrites every phase.
   */
  lobbyReady: boolean;
}

export interface PublicDealer {
  cards: PublicCard[];
  visibleValue: number | null;
  visibleSoft: boolean;
  /** True once the hole card is turned over. */
  revealed: boolean;
  /** Set after the peek resolves the round early on a dealer natural. */
  blackjack: boolean;
}

/** One human, who may hold several seats and stakes them all from one bankroll. */
export interface PublicPlayer {
  playerId: string;
  name: string;
  chips: number;
  connected: boolean;
  /** Every seat index this player occupies, in table order. */
  seats: number[];
  isHost: boolean;
  /**
   * The hue this player picked on their profile, or null. A request, not a
   * seat colour: `resolveSeatHues` settles clashes so every seat stays apart.
   */
  color: ProfileColorId | null;
  /** True when a signed-in account sat down here — the profile badge. */
  hasProfile: boolean;
}

/** A countdown the server is enforcing, so clients can render the same clock. */
export interface PublicDeadline {
  /** What runs out: betting, insurance, or the acting player's turn. */
  kind: 'bet' | 'insurance' | 'action' | 'break';
  /** Wall-clock ms since epoch when the server will act. */
  endsAt: number;
}

/** How much the TV dealer calls: nothing, the decisions, or everything. */
export type CalloutMode = 'off' | 'essential' | 'full';
export const CALLOUT_MODES: readonly CalloutMode[] = ['off', 'essential', 'full'];

/** The snapshot every client may see. Contains no hidden card data. */
export interface RoomState {
  code: string;
  /** Which game is on the felt. Blackjack unless the host switched it. */
  gameType: GameType;
  phase: Phase;
  seats: PublicSeat[];
  players: PublicPlayer[];
  dealer: PublicDealer;
  activeSeatIndex: number | null;
  /**
   * The player who runs the table. Explicitly held, not derived: it only moves
   * when the host transfers it or drops off the table entirely.
   */
  hostPlayerId: string | null;
  /** The host's first seat, for clients that highlight by seat. */
  hostSeatIndex: number | null;
  /**
   * True once the host has been offline past their grace window with nobody
   * connected to inherit the table: any player here may take the role with
   * `host_claim` instead of waiting the table out.
   */
  hostClaimable: boolean;
  /** Whether the TV display is attached. Purely informational. */
  displayConnected: boolean;
  /**
   * Host toggle: while set, every player card broadcasts as a card back, the
   * way some casinos play the hands. The dealer's upcard and the Hold'em
   * board still show.
   */
  cardsHidden: boolean;
  /**
   * Host toggle: while set, the TV shows the casino leaderboard as its view
   * instead of the live felt. Empty tables already show the board; this is
   * how the host puts it up mid-session.
   */
  leaderboardShown: boolean;
  /**
   * Host toggle: while set, the TV puts the large join QR on stage — the same
   * card an empty table shows — over the felt. Any phase.
   */
  qrShown: boolean;
  /**
   * Host toggle: whether the TV (or the cast device) plays its sound cues.
   * Starts off, like the TV's own button. The TV follows every change, and a
   * tap on the TV itself still overrides it locally until the host flips it
   * again.
   */
  soundOn: boolean;
  /** Host slider, 0–100: how loud the TV plays everything. */
  soundVolume: number;
  /** Host slider, 0–100: the casino-floor bed, on top of `soundVolume`. */
  ambienceVolume: number;
  /**
   * Host control: how much the TV dealer calls. `essential` calls only the
   * decisions; `full` calls everything. Captions follow it even with sound off.
   */
  calloutMode: CalloutMode;
  /** Host slider, 0–100: the dealer's voice, on top of `soundVolume`. */
  voiceVolume: number;
  /** Cards left in the shoe, for the TV shoe indicator. */
  shoeRemaining: number;
  /** Total cards in a full shoe under the current rules, for a depth gauge. */
  shoeSize: number;
  round: number;
  rules: TableRules;
  holdemRules: HoldemRules;
  baccaratRules: BaccaratRules;
  rouletteRules: RouletteRules;
  crapsRules: CrapsRules;
  threecardRules: ThreecardRules;
  kenoRules: KenoRules;
  /** The Hold'em table, or null unless the room is dealing Hold'em. */
  holdem: PublicHoldem | null;
  /** The baccarat table, or null unless the room is dealing baccarat. */
  baccarat: PublicBaccarat | null;
  /** The roulette table, or null unless the room is spinning roulette. */
  roulette: PublicRoulette | null;
  /** The craps table, or null unless the room is shooting dice. */
  craps: PublicCraps | null;
  /** The three-card poker table, or null unless the room is dealing it. */
  threecard: PublicThreecard | null;
  /** The keno board, or null unless the room is drawing keno. */
  keno: PublicKeno | null;
  /** The active countdown, or null when nothing is on the clock. */
  deadline: PublicDeadline | null;
  /**
   * The open "what should we play?" vote, or null when nobody is polling.
   * Only ever open between hands — the same window the host may switch the
   * felt by hand — and it clears the moment it resolves.
   */
  gamePoll: PublicGamePoll | null;
  /**
   * Party mode: the lineup the table is working through on its own, or null
   * when the host is picking games by hand.
   */
  party: PublicParty | null;
  /**
   * Rule changes the host made mid-round, already normalized. They land the
   * next time the table is between rounds; `rules` and friends stay the rules
   * in force until then.
   */
  pendingRules: PendingRules;
  /**
   * A game the host picked mid-hand. The felt switches to it when the hand
   * ends. Null when nothing is waiting.
   */
  pendingGame: GameType | null;
}

/** One hand as its owner sees it: real cards, real totals. */
export interface PrivateHandView {
  cards: Card[];
  value: number;
  soft: boolean;
  bet: number;
  status: HandStatus;
}

/** Sent to exactly one player socket: that player's own cards, seat by seat. */
export interface PrivateSeatHands {
  seatIndex: number;
  hands: PrivateHandView[];
  activeHandIndex: number | null;
}

/** One seat's Hold'em hole cards, as only its owner sees them. */
export interface PrivateHoldemSeat {
  seatIndex: number;
  cards: Card[];
  /** Best five so far, once the board makes five cards available. */
  rank: { category: HandCategory; label: string } | null;
}

/** One seat's three-card poker hand, as only its owner sees it. */
export interface PrivateThreecardSeat {
  seatIndex: number;
  cards: Card[];
  /** The hand's name and category once three cards are down. */
  rank: { category: ThreecardCategory; label: string } | null;
  /** Chips riding on Pair Plus next to the ante. */
  pairPlus: number;
}

/** The full private payload for one player across every seat they hold. */
export interface PrivateHand {
  playerId: string;
  /** Blackjack hands. Empty while the table is dealing Hold'em. */
  seats: PrivateSeatHands[];
  /** Hold'em hole cards. Absent while the table is dealing blackjack. */
  holdem?: PrivateHoldemSeat[];
  /** Three-card poker hands. Absent while the table is dealing anything else. */
  threecard?: PrivateThreecardSeat[];
}

// --- Hold'em public state --------------------------------------------------

/** What one seat is doing in the current Hold'em hand. */
export interface PublicHoldemSeat {
  index: number;
  /** Dealt into this hand. False for empty seats and anyone sitting out. */
  inHand: boolean;
  folded: boolean;
  allIn: boolean;
  /** Chips pushed out on the current street. */
  committed: number;
  /** Chips pushed out across the whole hand, street commitments included. */
  totalCommitted: number;
  /** Hole cards, face-down until the hand is shown. */
  cards: PublicCard[];
  /** True once this seat's cards are turned over at showdown. */
  revealed: boolean;
  /** Legal moves; null unless this seat is on the clock. */
  options: HoldemOptions | null;
  /** Whether this seat has already acted on the current street. */
  acted: boolean;
  /** Set at showdown for a revealed hand: what they ended up with. */
  handLabel: string | null;
  /** The five cards that made the hand, for the TV to highlight. */
  bestCards: Card[] | null;
  /** Chips won this hand, set once the pots are pushed. */
  won: number | null;
}

/** The last action taken, so the TV can narrate the table. */
export interface PublicHoldemAction {
  seatIndex: number;
  action: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all_in' | 'post_blind' | 'post_ante';
  /** Chips the action cost. */
  amount: number;
}

export interface PublicHoldem {
  /** Community cards, face-down only while the host has the table hidden. */
  board: PublicCard[];
  /** Street the table is on, or null between hands. */
  street: HoldemStreet | null;
  /** Main pot first, then each side pot. */
  pots: PotShare[];
  /** Everything on the table: settled pots plus live street commitments. */
  potTotal: number;
  /** The street's high commitment; what a call has to match. */
  currentBet: number;
  /** The last full raise, which is the smallest a re-raise may be. */
  minRaise: number;
  buttonSeatIndex: number | null;
  smallBlindSeatIndex: number | null;
  bigBlindSeatIndex: number | null;
  /** Blinds in force this hand; they climb when the host sets an increase. */
  smallBlind: number;
  bigBlind: number;
  seats: PublicHoldemSeat[];
  lastAction: PublicHoldemAction | null;
  /** Set once the hand settles: who won what, in pot order. */
  results: PublicHoldemResult[] | null;
  /** True when the hand ended with everyone but one player folding. */
  wonByFold: boolean;
}

/** One pot's outcome at settlement. */
export interface PublicHoldemResult {
  /** 0 is the main pot; 1 and up are side pots. */
  potIndex: number;
  amount: number;
  /** Seats that took a share, in table order. */
  winners: number[];
  /** The winning hand's name, or null when everyone else folded. */
  label: string | null;
}

// --- baccarat public state -------------------------------------------------

/** One seat's pair side bet. */
export interface PublicBaccaratSideBet {
  id: BaccaratSideBetId;
  stake: number;
  /** Chips returned on settle; null before the coup resolves. */
  payout: number | null;
}

/**
 * What one seat has backed this coup. The stake itself lives on `PublicSeat`,
 * shared with the other two games — only the spot is baccarat's own.
 */
export interface PublicBaccaratSeat {
  index: number;
  /** Which spot the seat backed, or null while it has not bet. */
  spot: BaccaratSpot | null;
  sideBets: PublicBaccaratSideBet[];
}

/** One settled main bet, for the TV's payout column. */
export interface PublicBaccaratResult {
  seatIndex: number;
  spot: BaccaratSpot;
  stake: number;
  /** Chips returned, stake included. 0 on a loss, the stake back on a push. */
  payout: number;
}

/**
 * The baccarat table. There are no private cards in this game: both hands are
 * community property, so everything here is public by construction.
 */
export interface PublicBaccarat {
  playerCards: PublicCard[];
  bankerCards: PublicCard[];
  /** Null while the host has the table hidden, or before the deal. */
  playerTotal: number | null;
  bankerTotal: number | null;
  /** Set once the coup resolves. */
  outcome: BaccaratOutcome | null;
  /** True when the coup ended on a two-card 8 or 9. */
  natural: boolean;
  resolved: boolean;
  seats: PublicBaccaratSeat[];
  /** Set once the coup settles: who backed what, and what it paid. */
  results: PublicBaccaratResult[] | null;
  /** Past coup outcomes, oldest first — the TV's bead road. */
  history: BaccaratOutcome[];
}

// --- roulette public state --------------------------------------------------

/** One chip stack a seat has placed: a bet id, its straight-up target, a stake. */
export interface PublicRouletteBet {
  id: RouletteBetId;
  /** The straight-up target; absent for every outside bet. */
  num?: RouletteBetNum;
  amount: number;
  /** Chips returned on settle, stake included; null before the spin lands. */
  payout: number | null;
}

/**
 * What one seat has spread across the layout this window. The seat's total
 * stake mirrors onto the shared `PublicSeat.bet`, like every other game.
 */
export interface PublicRouletteSeat {
  index: number;
  /** True once the seat locked in with Done. */
  done: boolean;
  bets: PublicRouletteBet[];
  /**
   * The spread this seat had down when its last spin settled, for rebet.
   * Empty until the seat's first spin; carried across the lobby resets.
   */
  lastBets: RouletteBetSpec[];
}

/** One settled seat, for the TV's payout column. */
export interface PublicRouletteResult {
  seatIndex: number;
  /** Everything the seat staked across its stacks. */
  stake: number;
  /** Chips returned across every stack, stake included. */
  payout: number;
}

/**
 * The roulette table. There is nothing to hide — bets and the pocket are all
 * public — so this sliver, like baccarat's, is public by construction.
 */
export interface PublicRoulette {
  /** The pocket the ball landed on, or null before the spin. */
  pocket: RoulettePocket | null;
  seats: PublicRouletteSeat[];
  /** Set once the spin settles: who staked what, and what came back. */
  results: PublicRouletteResult[] | null;
  /** Past pockets, oldest first — the TV's history ribbon. */
  history: RoulettePocket[];
  spinNumber: number;
}

// --- keno public state ------------------------------------------------------

/**
 * One ticket a seat has written for the current window: its spots, its stake,
 * and — once the draw lands — what it caught and what came back.
 */
export interface PublicKenoTicket {
  /** Server-assigned, unique per seat per window. */
  id: number;
  /** The picked numbers, in the order they were tapped. */
  spots: number[];
  amount: number;
  /** The picked numbers the draw hit, ascending; null before the draw. */
  catches: number[] | null;
  /** Chips returned on settle, stake included; null before the draw. */
  payout: number | null;
}

/** What one seat has written for this window. The total mirrors `Seat.bet`. */
export interface PublicKenoSeat {
  index: number;
  /** True once the seat locked in with Done. */
  done: boolean;
  tickets: PublicKenoTicket[];
  /**
   * The slate this seat had down when its last draw settled, for rebet.
   * Empty until the seat's first draw; carried across the lobby resets.
   */
  lastTickets: { spots: number[]; amount: number }[];
}

/** One settled seat, for the TV's payout column. */
export interface PublicKenoResult {
  seatIndex: number;
  /** Everything the seat staked across its tickets. */
  stake: number;
  /** Chips returned across every ticket, stake included. */
  payout: number;
  /**
   * The seat's best-paying ticket this draw, when any of them paid — a 10-spot
   * consolation on zero catches counts.
   */
  topTicket: { id: number; catches: number; payout: number } | null;
}

/**
 * The keno board. There is nothing to hide — tickets and the draw are all
 * public — so this sliver, like baccarat's and roulette's, is public by
 * construction.
 */
export interface PublicKeno {
  /** The twenty numbers, draw order; null before the draw. */
  draw: number[] | null;
  seats: PublicKenoSeat[];
  /** Set once the draw settles: who staked what, and what came back. */
  results: PublicKenoResult[] | null;
  /** Past draws, oldest first — the TV's history and heat board. */
  history: number[][];
  drawNumber: number;
}

// --- craps public state -----------------------------------------------------

/** One stake on the craps layout: a bet id, its amount, its working flag. */
export interface PublicCrapsBet {
  id: CrapsBetId;
  amount: number;
  /** Place and hardway bets only: true when called working on the come-out. */
  working: boolean;
  /** Place and hardway bets only: what a hit does — down, same bet, or press. */
  press: CrapsPressMode;
  /** Chips returned by the last roll that settled it; null while it rides. */
  payout: number | null;
}

/** What one seat has on the felt this hand. */
export interface PublicCrapsSeat {
  index: number;
  /** True once the seat locked in for the come-out window. */
  ready: boolean;
  bets: PublicCrapsBet[];
  /** Chips this seat has won or lost across the rolls of this hand. */
  net: number;
}

/**
 * What one roll did to one stake, for the TV's call-out and the phone's
 * running commentary. Only the bets the roll actually touched appear.
 */
export interface PublicCrapsSettlement {
  seatIndex: number;
  betId: CrapsBetId;
  /** The stake that was on the felt, before the roll settled it. */
  amount: number;
  resolution: CrapsResolution;
  /** Chips returned, stake included; 0 on a loss. */
  payout: number;
  /** Chips the win left on the spot: a same bet or a press. 0 when it came down. */
  rides: number;
}

/** One settled seat, for the TV's payout column. */
export interface PublicCrapsResult {
  seatIndex: number;
  /** Everything the seat staked across the whole hand. */
  stake: number;
  /** Chips returned across the whole hand, stake included. */
  payout: number;
}

/** The session's longest hand: who was shooting and how many rolls it ran. */
export interface PublicCrapsLongestHand {
  seatIndex: number | null;
  rolls: number;
}

/**
 * The craps table. Dice and stakes are all public — nobody holds a card — so
 * this sliver, like baccarat's and roulette's, is public by construction.
 */
export interface PublicCraps {
  /** The point, or null on the come-out: the puck is OFF. */
  point: number | null;
  /** The seat holding the dice. */
  shooterSeatIndex: number | null;
  /** The dice as they last landed, or null before the first roll. */
  lastRoll: CrapsRoll | null;
  /** What that roll did to the hand; null before the first roll. */
  lastEvent: CrapsRollEvent | null;
  /** Recent rolls, oldest first — the TV's roll ribbon. */
  rollHistory: CrapsRoll[];
  /** What the last roll paid and took, bet by bet. Empty before the first roll. */
  lastSettlements: PublicCrapsSettlement[];
  seats: PublicCrapsSeat[];
  /** Set once the hand settles: who staked what, and what came back. */
  results: PublicCrapsResult[] | null;
  /** Rolls in the hand so far, for the TV's "hot hand" counter. */
  rollNumber: number;
  /** The longest hand of the session so far, or null before the first one ends. */
  longestHand: PublicCrapsLongestHand | null;
  /** How many times each total has landed this session; read totals 2–12. */
  totalTally: number[];
}

// --- three-card poker public state -------------------------------------------

/**
 * What one seat has riding this hand. The ante lives on the shared
 * `PublicSeat.bet`; the play bet, the fold and the Pair Plus stake are this
 * game's own.
 */
export interface PublicThreecardSeat {
  index: number;
  /** The three cards, face-down until the showdown turns played hands over. */
  cards: PublicCard[];
  /** True once the seat mucked: the ante is gone, the cards stay hidden. */
  folded: boolean;
  /** The play bet staked to stay in; 0 until the seat decides. */
  play: number;
  /** The seat's own hand name, visible once its cards are. */
  handLabel: string | null;
  /** Pair Plus stake, and what it came back once the hand settles. */
  pairPlus: { stake: number; payout: number | null } | null;
}

/** One settled seat, for the TV's payout column. */
export interface PublicThreecardResult {
  seatIndex: number;
  ante: number;
  play: number;
  /** Chips returned across ante, play, ante bonus and Pair Plus. */
  payout: number;
  outcome: 'win' | 'loss' | 'push' | 'fold';
}

/**
 * The three-card poker table. Each player's three cards are private until the
 * showdown — the only hidden information in the game — so the public sliver
 * carries them face-down and the hands travel in the private payload.
 */
export interface PublicThreecard {
  dealerCards: PublicCard[];
  /** Set once the dealer's cards are up: queen-high or better. */
  dealerQualifies: boolean | null;
  /** The dealer's hand name, once shown. */
  dealerLabel: string | null;
  resolved: boolean;
  seats: PublicThreecardSeat[];
  /** Set once the hand settles: who staked what, and what came back. */
  results: PublicThreecardResult[] | null;
  /** Past hands, oldest first — the TV's history strip. */
  history: ThreecardOutcome[];
  handNumber: number;
}

export const MAX_SEATS = 7;
export const STARTING_CHIPS = 1000;
export const MAX_NAME_LENGTH = 12;

export type ErrorCode =
  | 'room_not_found'
  | 'room_full'
  | 'name_taken'
  | 'name_taken_offline'
  | 'name_reserved'
  /** The membership id presented did not come with its token. */
  | 'member_forbidden'
  | 'bad_request'
  | 'not_host'
  | 'not_seated'
  | 'wrong_phase'
  | 'not_your_turn'
  | 'seat_taken'
  | 'seat_limit'
  | 'insufficient_chips'
  | 'wrong_game'
  | 'already_seated'
  | 'not_your_roll'
  /** Craps: the last roll is still settling; the dice are not back yet. */
  | 'dice_settling'
  | 'bet_closed'
  | 'over_max'
  /** A vote arrived with no poll open, or a poll was opened on top of one. */
  | 'no_poll'
  | 'poll_open'
  /** Party mode is choosing the games; stop it before picking by hand. */
  | 'party_on';

export interface RoomError {
  code: ErrorCode;
  message: string;
}

// --- casino stats surface (Phase 5d) ----------------------------------------

/**
 * One member's lifetime counters for one game, as the REST surface answers.
 * Blackjack fills the hand counters; Hold'em fills `potsWon` / `showdowns`;
 * baccarat, roulette and craps fill hands / wins / losses / pushes and net.
 * A game never played answers with every field zeroed, not a missing row.
 */
export interface MemberStatsView {
  hands: number;
  wins: number;
  losses: number;
  pushes: number;
  blackjacks: number;
  surrenders: number;
  doubles: number;
  splits: number;
  sideBetsWon: number;
  potsWon: number;
  showdowns: number;
  /** High-water mark: the largest single-hand profit. */
  biggestWin: number;
  /** High-water mark: the largest whole pot won (Hold'em). */
  biggestPot: number;
  /** Signed lifetime result: chips won minus chips staked. */
  net: number;

  // --- volume ---------------------------------------------------------------
  /**
   * Settled rounds. Distinct from `hands`: one blackjack round can deal a
   * seat several hands after a split, and one craps round runs many rolls.
   */
  rounds: number;
  /** Every chip ever pushed out, losing wagers included. */
  wagered: number;
  /** Every chip ever returned, stakes included. `returned - wagered === net`. */
  returned: number;

  // --- extremes -------------------------------------------------------------
  /** High-water mark: the worst single-round loss, as a positive number. */
  biggestLoss: number;
  /** High-water mark: the best whole-round result. */
  bestRound: number;
  /** Low-water mark: the worst whole-round result, signed negative. */
  worstRound: number;

  // --- streaks --------------------------------------------------------------
  /** Winning rounds in a row right now. A push leaves a streak standing. */
  winStreak: number;
  lossStreak: number;
  bestWinStreak: number;
  worstLossStreak: number;

  // --- per-game colour ------------------------------------------------------
  /** Side bets placed, win or lose; `sideBetsWon` counts the ones that paid. */
  sideBetsPlaced: number;
  /** Blackjack: hands that went over 21. */
  busts: number;
  /** Hold'em: hands mucked before showdown. */
  folds: number;
  /** Hold'em: hands the member was all-in on. */
  allIns: number;
  /** Hold'em: hands entered with chips in voluntarily — blinds do not count. */
  vpip: number;
  /** Baccarat: coups decided by a two-card natural. */
  naturals: number;
  /** Roulette: straight-up numbers that came in. */
  straightUpHits: number;
  /** Keno: tickets played, win or lose. */
  ticketsPlaced: number;
  /** Keno: high-water mark, the most numbers one ticket ever caught. */
  bestCatch: number;
  /** Craps: points made while holding the dice. */
  pointsMade: number;
  /** Craps: hands ended by sevening out while holding the dice. */
  sevenOuts: number;
  /** Craps: dice thrown as the shooter. */
  rollsShot: number;

  // --- when -----------------------------------------------------------------
  /** ISO timestamps of the first and last settled round. Null before any. */
  firstPlayedAt: string | null;
  lastPlayedAt: string | null;
}

/**
 * Every game plus the cross-game totals, keyed the way the surfaces read
 * them. `totals` is itself a full `MemberStatsView` — the per-game rows
 * folded together — so anything that renders one game renders the totals.
 */
export interface MemberStatsBundle {
  games: Record<GameType, MemberStatsView>;
  totals: MemberStatsView;
}

/** The owner's drill-down: `GET /api/casinos/:code/members/:memberId/stats`. */
export interface MemberStatsResponse extends MemberStatsBundle {
  casinoId: string;
  memberId: string;
}

/**
 * The phone's own view of its membership: balance plus stats, fetched with
 * the `memberId` the phone already holds. The id is the capability — the
 * same living-room trust model as the TV's display token.
 */
export interface CasinoMemberView extends MemberStatsBundle {
  casinoId: string;
  memberId: string;
  name: string;
  chips: number;
  /** Chips this member has ever been staked, for the "up/down" line. */
  buyIn: number;
  /** The hue this member picked on their profile, or null if they never did. */
  color: ProfileColorId | null;
}

/**
 * One casino this account has actually sat down in, under its host seat.
 * The profile's play history is built from these: a membership per venue,
 * with the balance it holds and what it did there.
 */
export interface ProfileCasinoPlay {
  casinoId: string;
  casinoName: string;
  joinCode: string;
  memberId: string;
  chips: number;
  /** Chips the house staked this seat, for the up/down line. */
  buyIn: number;
  /** True when the signed-in account also owns this venue. */
  owned: boolean;
  lastSeenAt: string;
  totals: MemberStatsView;
}

/**
 * `GET /api/me/play` — every venue this account has played, newest first,
 * plus the cross-casino fold.
 *
 * Only seats keyed to the account are here. A night played from a guest phone
 * opens an anonymous membership with no account behind it, so it cannot be
 * rolled up and is deliberately absent rather than guessed at.
 */
export interface ProfilePlayResponse {
  casinos: ProfileCasinoPlay[];
  totals: MemberStatsView;
}

/**
 * `GET /api/me/referrals` — the account's referral code and what it has
 * brought in.
 *
 * - `impressions`: distinct people who joined a casino this account owns.
 *   A signed-in player counts once across all of them; a guest phone holds a
 *   separate membership per casino, so it counts once per casino.
 * - `signups`: accounts opened with this code.
 * - `owners`: those accounts that have since opened a casino of their own.
 */
export interface ReferralSummary {
  code: string;
  impressions: number;
  signups: number;
  owners: number;
}

/** One row of the public casino leaderboard, best net first. */
export interface CasinoLeaderboardEntry {
  memberId: string;
  name: string;
  /**
   * The hue this member picked on their profile, null until they pick one.
   * Public, like the name beside it: the board is what the room can already
   * read off the felt, and a colour is how a player asks to be recognised.
   */
  color: ProfileColorId | null;
  /** True once a signed-in account has played under this membership. */
  hasProfile: boolean;
  net: number;
  hands: number;
  /** The member's best and worst single round of the night. */
  bestRound: number;
  worstRound: number;
  /** Winning rounds in a row right now, across every game. */
  winStreak: number;
  /** Everything they have pushed out, for the "biggest action" read. */
  wagered: number;
  /** Live bankroll — the quote's last price on the chip desk. */
  chips: number;
  /**
   * Lifetime: what the house has staked them. Windowed reads: the balance the
   * member held when the window opened, so `chips - buyIn` is the window's
   * move and the desk's change % reads against the right base.
   */
  buyIn: number;
  /** Cross-game totals the chip desk's ROI / win-rate metrics read. */
  totals: MemberStatsView;
  /** The game this member played most, or null before a first settle. */
  favoriteGame: GameType | null;
}
